/**
 * Rl31ActionModal — Demande de Relevé 31 au cabinet comptable.
 *
 * Le client envoie une demande au cabinet, qui se charge de la génération du PDF
 * officiel et de la transmission à Revenu Québec.
 *
 * Workflow : portail (saisie locataires) → PMS Files cabinet → Générer PDF →
 * Vérification → Marquer traitée + notif client.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import {
  Receipt, CheckCircle2, Loader2, Send, UserPlus, Trash2,
} from "lucide-react";
import { BaseActionModal, FieldLabel, API, authH } from "./shared";

const blankTenant = () => ({
  name: "",
  address: "",
  total_rent_paid: "",
  has_daycare_service: false,
  daycare_amount: "",
  months_rented: 12,
});

export function Rl31ActionModal({ buildings, onClose, onReload }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const [propertyId, setPropertyId] = useState("");
  const [note, setNote] = useState("");
  const [tenants, setTenants] = useState([blankTenant()]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const updateTenant = (idx, field, value) => {
    setTenants((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const addTenant = () => setTenants((prev) => [...prev, blankTenant()]);
  const removeTenant = (idx) => setTenants((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const submitRequest = async () => {
    const validTenants = tenants.filter((t) => (t.name || "").trim());
    if (validTenants.length === 0) {
      toast.error("Ajoutez au moins un locataire avec un nom");
      return;
    }
    for (const t of validTenants) {
      const rent = Number(t.total_rent_paid || 0);
      if (rent <= 0) {
        toast.error(`Loyer payé manquant ou invalide pour ${t.name}`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        year,
        property_id: propertyId || null,
        note,
        tenants: validTenants.map((t) => ({
          name: t.name.trim(),
          address: t.address.trim(),
          total_rent_paid: Number(t.total_rent_paid || 0),
          has_daycare_service: !!t.has_daycare_service,
          daycare_amount: t.has_daycare_service ? Number(t.daycare_amount || 0) : 0,
          months_rented: Number(t.months_rented || 12),
        })),
      };
      await axios.post(`${API}/portal/immeuble/rl31-request`, payload, { headers: authH() });
      toast.success(`Demande RL-31 envoyée à votre comptable (${validTenants.length} locataire${validTenants.length > 1 ? 's' : ''})`);
      setSent(true);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BaseActionModal
      title="Relevé 31 (RL-31)"
      subtitle="Demande à votre équipe"
      tone="amber"
      icon={Receipt}
      testId="immeuble-action-modal-rl31"
      onClose={onClose}
      footer={
        sent ? (
          <button onClick={onClose} className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 text-white" data-testid="immeuble-rl31-close-success">Fermer</button>
        ) : (
          <>
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="immeuble-rl31-cancel">Annuler</button>
            <button
              onClick={submitRequest}
              disabled={busy}
              className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="immeuble-rl31-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Envoyer au cabinet
            </button>
          </>
        )
      }
    >
      {sent ? (
        <div className="text-center py-6 space-y-3" data-testid="immeuble-rl31-success">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <p className="font-semibold text-slate-800">Demande envoyée ✓</p>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Votre équipe a été notifiée et examinera votre demande. Une fois <b>acceptée</b>,
            les RL-31 {year} seront générés par votre comptable, vérifiés puis transmis
            à Revenu Québec et déposés dans votre coffre-fort.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] leading-snug text-amber-900">
            <b>Workflow</b> — Remplissez les infos de chaque locataire ci-dessous.
            Votre comptable reçoit la demande dans son tableau de bord, vérifie les
            informations, génère les RL-31 officiels puis les transmet à Revenu Québec.
          </div>

          <div className="grid grid-cols-2 gap-2">
            <FieldLabel label="Année fiscale" required>
              <input
                type="number"
                min="2010"
                max={currentYear}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="cc-input font-mono"
                data-testid="immeuble-rl31-year"
              />
            </FieldLabel>
            <FieldLabel label="Immeuble (optionnel)">
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="cc-input"
                data-testid="immeuble-rl31-property-select"
              >
                <option value="">— Tous —</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name || b.address_line}</option>
                ))}
              </select>
            </FieldLabel>
          </div>

          <div className="space-y-3" data-testid="immeuble-rl31-tenants-list">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Locataires à déclarer ({tenants.length})
              </h4>
              <button
                onClick={addTenant}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="immeuble-rl31-add-tenant"
              >
                <UserPlus className="w-3 h-3" />
                Ajouter
              </button>
            </div>
            {tenants.map((t, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2" data-testid={`immeuble-rl31-tenant-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-600">Locataire #{idx + 1}</span>
                  {tenants.length > 1 && (
                    <button
                      onClick={() => removeTenant(idx)}
                      className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1 rounded"
                      data-testid={`immeuble-rl31-remove-tenant-${idx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FieldLabel label="Nom complet" required>
                    <input
                      type="text"
                      value={t.name}
                      onChange={(e) => updateTenant(idx, "name", e.target.value)}
                      className="cc-input"
                      placeholder="Prénom Nom"
                      data-testid={`immeuble-rl31-tenant-${idx}-name`}
                    />
                  </FieldLabel>
                  <FieldLabel label="Loyer total payé ($)" required>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={t.total_rent_paid}
                      onChange={(e) => updateTenant(idx, "total_rent_paid", e.target.value)}
                      className="cc-input font-mono"
                      placeholder="0.00"
                      data-testid={`immeuble-rl31-tenant-${idx}-rent`}
                    />
                  </FieldLabel>
                </div>
                <FieldLabel label="Adresse du logement loué">
                  <input
                    type="text"
                    value={t.address}
                    onChange={(e) => updateTenant(idx, "address", e.target.value)}
                    className="cc-input"
                    placeholder="123 Rue, app. 4, Ville, Province  H1A 1A1"
                    data-testid={`immeuble-rl31-tenant-${idx}-address`}
                  />
                </FieldLabel>
                <div className="grid grid-cols-3 gap-2 items-end">
                  <FieldLabel label="Mois loués">
                    <input
                      type="number"
                      min="1" max="12"
                      value={t.months_rented}
                      onChange={(e) => updateTenant(idx, "months_rented", e.target.value)}
                      className="cc-input font-mono"
                      data-testid={`immeuble-rl31-tenant-${idx}-months`}
                    />
                  </FieldLabel>
                  <div className="flex items-center gap-2 pb-1">
                    <input
                      type="checkbox"
                      id={`daycare-${idx}`}
                      checked={t.has_daycare_service}
                      onChange={(e) => updateTenant(idx, "has_daycare_service", e.target.checked)}
                      data-testid={`immeuble-rl31-tenant-${idx}-daycare-toggle`}
                    />
                    <label htmlFor={`daycare-${idx}`} className="text-[11px] text-slate-700">
                      Service de garde inclus
                    </label>
                  </div>
                  {t.has_daycare_service && (
                    <FieldLabel label="Montant garderie ($)">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={t.daycare_amount}
                        onChange={(e) => updateTenant(idx, "daycare_amount", e.target.value)}
                        className="cc-input font-mono"
                        placeholder="0.00"
                        data-testid={`immeuble-rl31-tenant-${idx}-daycare-amount`}
                      />
                    </FieldLabel>
                  )}
                </div>
              </div>
            ))}
          </div>

          <FieldLabel label="Note pour l'équipe (optionnel)">
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="ex: Locataire #2 a quitté en juin"
              className="cc-input"
              data-testid="immeuble-rl31-note"
            />
          </FieldLabel>
        </div>
      )}
    </BaseActionModal>
  );
}
